import { RefinerFormDefaultId, LocationAutocomplete, callAutocomplete } from './chunk-6IXBTFSD.js';
import { useInitialCommuteLocation } from './chunk-LOGDY5AH.js';
import { useSearchContext, useModalContext, useTranslate, useConditionalFilter, useTemplates, useLocale, useRelation, useServiceUrl, useDefaultLocation } from './chunk-UUWHMO43.js';
import { Box, useBreakpointValue, useTokens } from '@gemini/core';
import { Button, ToggleButton, CellContent, ButtonGroup, Dropdown, Tabs, Modal, CheckboxGroup, Divider, TextField, Chip, Tag, Autocomplete, Checkbox } from '@gemini/ui';
import React, { forwardRef, useState, useEffect, useRef, Suspense } from 'react';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { CarIcon, BicycleIcon, WalkingIcon, TramIcon, ChevronRightIcon, EuroSignIcon, LocationDotIcon, LocationCircleIcon, LocationDotFilledIcon, TramFilledIcon, ClockIcon, LocationCircleFilledIcon, BusIcon, CloseIcon } from '@gemini/icons';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { CommuteMode, FormFeatureType, ThreeStateType, ListingsDisplayType, FormProjectType, ROOMS_DICTIONARY, RoomsType, FormLocationType, PlaceType } from '@aviv-search/types';
import _2, { isEmpty, maxBy, upperFirst, chain, intersection } from 'lodash';
import { useDebounce } from 'react-use';
import axios from 'axios';
import { encode } from '@mapbox/polyline';
import circleToPolygon from 'circle-to-polygon';

var useScrollInto = (scrollInto, refs) => {
  useEffect(() => {
    if (scrollInto) {
      const ref = refs.current[scrollInto];
      if (ref) {
        ref.style.scrollMargin = "10px";
        ref.scrollIntoView({ block: "start" });
      }
    }
  }, []);
};

// src/containers/MultiLocationAutocompleteModal/isCountButtonDisabled.ts
var isCountButtonDisabled = (locations) => (locations?.length ?? 0) < 1;
var FilterButtonBaseTestId = "search-mfe-filter-button-base-testid";
var FilterButtonBase = ({
  label,
  ariaLabel = label,
  forwardedRef,
  value,
  icon,
  isShortcut = false,
  onPress = () => {
  },
  children,
  testId
}) => {
  const isSelected = value !== void 0;
  const buttonContent = children || `${value ?? label}`;
  const buttonProps = {
    isFullWidth: true,
    "aria-label": ariaLabel,
    ref: forwardedRef,
    onPress,
    icon,
    testId: FilterButtonBaseTestId
  };
  return isShortcut ? (
    // FIXME : wait for Gemini to handle secondary-floating variant on a ToggleButton
    /* @__PURE__ */ jsx(Button, { ...buttonProps, size: "48", variant: "secondary-floating", icon, children: buttonContent })
  ) : /* @__PURE__ */ jsx(ToggleButton, { ...buttonProps, type: "button", alignment: "left", isSelected, testId, children: buttonContent });
};
var FilterButton = forwardRef((props, ref) => /* @__PURE__ */ jsx(FilterButtonBase, { ...props, forwardedRef: ref }));
var ModalBreakpoints = /* @__PURE__ */ ((ModalBreakpoints2) => {
  ModalBreakpoints2["base"] = "base";
  ModalBreakpoints2["md"] = "medium";
  return ModalBreakpoints2;
})(ModalBreakpoints || {});
var isIOS = () => {
  if (typeof navigator === "undefined" || !navigator.userAgent) {
    return false;
  }
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && typeof window.MSStream === "undefined";
};
var ModalContainer = ({
  title,
  trigger,
  children,
  testId,
  onOpen = () => {
  },
  onValidate = () => {
  },
  onCancel = () => {
  },
  onReset = () => {
  },
  footerEndButton,
  footerStartButton
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const breakpointValue = useBreakpointValue(ModalBreakpoints);
  const displayMode = breakpointValue === "base" /* base */ ? "fullscreen" : "fullHeight";
  const open = () => {
    onOpen();
    setIsOpen(true);
  };
  const close = (shouldCancel = false) => {
    if (shouldCancel)
      onCancel();
    else
      onValidate();
    setIsOpen(false);
  };
  const closeWithIosDelay = () => {
    if (isIOS()) {
      setTimeout(() => {
        close();
      }, 100);
    } else {
      close();
    }
  };
  return /* @__PURE__ */ jsx(
    Modal,
    {
      testId,
      trigger,
      isDismissable: true,
      hasCloseButton: true,
      title,
      isOpen,
      displayMode,
      onOpenChange: (changedIsOpen) => {
        if (changedIsOpen)
          open();
        else
          close(true);
      },
      footerEndButtonProps: {
        ...footerEndButton,
        onPress: () => closeWithIosDelay()
      },
      footerStartButtonProps: footerStartButton && { ...footerStartButton, onPress: () => onReset() },
      children
    }
  );
};
var getLabel = ({ labels, districts = [], boroughs = [] }) => {
  if (districts.length > 0)
    return districts[0].labels.join(", ");
  if (boroughs.length > 0)
    return boroughs[0].labels.join(", ");
  return labels.join(", ");
};
var getNumberOfLocations = (locations) => locations.reduce(
  (result, { districts = [], boroughs = [] }) => result + Math.max(districts.length + boroughs.length, 1),
  0
);
var getLocationTitle = ({
  formModel: { locations },
  customTitles: { commuteLabel }
}) => {
  if (!locations || locations.length === 0)
    return "-";
  const [location] = locations;
  const { type } = location;
  if (type === FormLocationType.COMMUTE) {
    return commuteLabel;
  }
  if (type === FormLocationType.RADIUS) {
    return `${getLabel(location)} (+${location.radius}km)`;
  }
  const labels = [getLabel(location)];
  const numberOfLocations = getNumberOfLocations(locations);
  if (numberOfLocations > 1) {
    labels.push(`${useTranslate("location.button.badge.others", { count: numberOfLocations - 1 })}`);
  }
  return labels.join(" ");
};
var mapToCity = (parent, parents, id, labels, postalCodes, coordinates, variant) => ({
  id: parent.id || "",
  labels: parent.labels || [],
  postalCodes: parent.postalCodes || [],
  type: FormLocationType.CITY,
  coordinates: parent.coordinates,
  parentIds: parents.filter(({ id: parentId }) => parentId && parentId !== parent.id).map(({ id: parentId }) => parentId),
  [variant]: [{ id, labels, postalCodes, coordinates }]
});
var shouldKeep = ({ id: locationId, parentIds: locationParentIds }, newLocationId, newLocationParents) => {
  const newLocationIsParentOfLocation = locationParentIds.includes(newLocationId);
  const isParentOfNewLocation = newLocationParents.findIndex(({ id }) => id === locationId) >= 0;
  return locationId !== newLocationId && !newLocationIsParentOfLocation && !isParentOfNewLocation;
};
var mergeLocations = (formLocations, { id, type_key, labels, postalCodes, coordinates, parents }) => {
  const locations = [...formLocations];
  const countryId = parents.find((parent) => parent.type_key === PlaceType.COUNTRY)?.id;
  if (countryId) {
    const countryIndex = locations.findIndex((location) => location.id === countryId);
    if (countryIndex >= 0)
      locations.splice(countryIndex, 1);
  }
  if (type_key === PlaceType.NEIGHBORHOOD || type_key === PlaceType.BOROUGHS) {
    const variant = type_key === PlaceType.NEIGHBORHOOD ? "districts" : "boroughs";
    const parentCity = parents.find(({ type_key: parentTypeKey }) => parentTypeKey === PlaceType.MUNICIPALITY);
    const cityIndex = locations.findIndex(({ id: cityId }) => cityId === parentCity?.id);
    if (cityIndex >= 0) {
      const location = locations[cityIndex];
      const updatedCity = {
        ...location,
        [variant]: [
          ...location[variant] || [],
          {
            id,
            labels,
            postalCodes,
            coordinates
          }
        ]
      };
      locations.splice(cityIndex, 1, updatedCity);
      return locations;
    }
    if (parentCity) {
      const city = mapToCity(parentCity, parents, id, labels, postalCodes, coordinates, variant);
      return [...locations, city];
    }
  }
  const newLocations = locations?.filter((location) => shouldKeep(location, id, parents));
  const isCity = type_key === PlaceType.MUNICIPALITY;
  return [
    ...newLocations,
    {
      type: isCity ? FormLocationType.CITY : FormLocationType.ID,
      id,
      labels,
      postalCodes,
      coordinates,
      parentIds: (parents ?? []).map(({ id: parentId }) => parentId),
      radius: void 0,
      polyline: void 0
    }
  ];
};
var LocationAutocompleteWrapper = ({ isDisabled, locations, onChange }) => {
  const placeholder = useTranslate("location.autocomplete.textfield.placeholder.another");
  const ariaLabel = useTranslate("location.autocomplete.textfield.aria_label");
  const { locationTypes = [] } = useTemplates().filters;
  return /* @__PURE__ */ jsx(
    LocationAutocomplete,
    {
      isDisabled,
      selectedIds: locations.map(({ id, districts = [], boroughs = [] }) => [
        id,
        ...[...districts, ...boroughs].map(({ id: relationId }) => relationId)
      ]).flat(),
      onSubmit: (item) => {
        const { id } = item;
        if (!id || locations.find((location) => location.id === id))
          return;
        onChange(mergeLocations(locations, item));
      },
      placeholderText: placeholder,
      ariaLabelText: ariaLabel,
      locationTypes
    }
  );
};
var DumbLocationItemTestId = "searchmfe-dumb-location-item-testid";
var DumbLocationItemHeaderTestId = "searchmfe-dumb-location-item-header-testid";
var DumbLocationItemTagsTestId = "searchmfe-dumb-location-item-tags-testid";
var LocationHeader = ({ title, icon, onClose, ariaLabel }) => /* @__PURE__ */ jsxs(
  Box,
  {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "color.background.subdued",
    paddingLeft: "spacing.16",
    paddingTop: "spacing.8",
    paddingBottom: "spacing.8",
    paddingRight: "spacing.8",
    testId: DumbLocationItemHeaderTestId,
    children: [
      /* @__PURE__ */ jsxs(Box, { display: "flex", children: [
        React.createElement(icon),
        /* @__PURE__ */ jsx(Box, { as: "span", marginLeft: "spacing.8", children: title })
      ] }),
      /* @__PURE__ */ jsx(Box, { as: "button", "aria-label": ariaLabel, onClick: onClose, cursor: "pointer", borderWidth: "borderWidth.none", children: /* @__PURE__ */ jsx(CloseIcon, {}) })
    ]
  }
);
var LocationItemTags = ({ tags }) => {
  if (isEmpty(tags))
    return null;
  return /* @__PURE__ */ jsx(
    Box,
    {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      marginBottom: "spacing.16",
      gap: "spacing.8",
      width: "100%",
      transitionDuration: "motionDuration.150",
      testId: DumbLocationItemTagsTestId,
      children: tags.map(({ icon, label }) => /* @__PURE__ */ jsx(Tag, { icon, variant: "subdued", children: label }, label))
    }
  );
};
var DumbLocationItem = ({ headerProps, tags, modal }) => {
  const hasTags = !isEmpty(tags);
  return /* @__PURE__ */ jsxs(
    Box,
    {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      borderColor: "color.border.light.default",
      borderWidth: "borderWidth.1",
      borderRadius: "radius.16",
      borderStyle: "solid",
      overflow: "hidden",
      testId: DumbLocationItemTestId,
      children: [
        /* @__PURE__ */ jsx(LocationHeader, { ...headerProps }),
        /* @__PURE__ */ jsxs(
          Box,
          {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: modal ? "spacing.16" : "spacing.0",
            maxHeight: "max-content",
            children: [
              hasTags ? /* @__PURE__ */ jsx(LocationItemTags, { tags }) : null,
              /* @__PURE__ */ jsx(
                Box,
                {
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "space-between",
                  transitionDuration: "motionDuration.150",
                  children: modal && modal()
                }
              )
            ]
          }
        )
      ]
    }
  );
};
var DistrictsAndBoroughsChips = ({ cityId, defaultChipText }) => {
  const { control, getValues } = useFormContext();
  const locations = getValues("locations");
  const index = locations.findIndex(({ id }) => id === cityId);
  const location = locations.at(index);
  const { update } = useFieldArray({ name: "locations", control });
  const { labels: cityLabels = [], districts = [], boroughs = [] } = location;
  const onUnselect = (id) => update(index, {
    ...location,
    districts: districts.filter((district) => district.id !== id),
    boroughs: boroughs.filter((borough) => borough.id !== id)
  });
  const selectedItems = [...districts, ...boroughs];
  return /* @__PURE__ */ jsx(Box, { display: "flex", flexDirection: "row", flexWrap: "wrap", marginBottom: "spacing.12", gap: "spacing.8", children: selectedItems.length > 0 ? selectedItems.map(({ id, labels: [label] }) => /* @__PURE__ */ jsx(Chip, { isSelected: true, onClose: () => onUnselect(id), variant: "input", children: label }, id)) : /* @__PURE__ */ jsx(Chip, { isSelected: true, variant: "filter", children: `${cityLabels.join(", ")} (${defaultChipText})` }) });
};
var DistrictsOrBoroughsSelectorCheckBoxTestId = "Districts-or-boroughs-selector-checkbox-testid";
var DistrictsOrBoroughsSelectorCheckBoxGroupTestId = "Districts-or-boroughs-selector-checkbox-group-testid";
var DistrictsOrBoroughsSelector = ({ cityId, variant, relations }) => {
  const otherVariant = variant === "districts" ? "boroughs" : "districts";
  const { getValues, control } = useFormContext();
  const { update } = useFieldArray({ name: "locations", control });
  const locations = getValues("locations");
  const index = locations.findIndex((location2) => location2.id === cityId);
  const location = locations.at(index);
  const { labels: [cityLabel] = [] } = location;
  const variantItems = location[variant] || [];
  const otherVariantItems = location[otherVariant] || [];
  const allLabel = useTranslate(`modals.districts.checkbox_group.all_${variant}.label`);
  const onAllClick = (isSelected) => {
    if (isSelected)
      update(index, { ...location, [variant]: [], [otherVariant]: [] });
  };
  const onItemClick = (selectedItemIds) => {
    const selectedItems = relations.filter(({ id }) => selectedItemIds.includes(id));
    const selectedPostalCodes = chain(selectedItems).map("postalCodes").compact().flatten().uniq().value();
    const newVariantItems = selectedItems.map(({ labels, ...rest }) => ({ ...rest, labels: [...labels, cityLabel] }));
    const newOtherVariantItems = otherVariantItems.filter(
      ({ postalCodes = [] }) => intersection(selectedPostalCodes, postalCodes).length === 0
    );
    update(index, {
      ...location,
      [variant]: newVariantItems,
      [otherVariant]: newOtherVariantItems
    });
  };
  const checkboxGroupItems = relations.map(({ id, labels: [label] }) => ({ value: id, label }));
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Checkbox,
      {
        isSelected: variantItems.length === 0 && otherVariantItems.length === 0,
        hasNecessityIndicator: false,
        onChange: onAllClick,
        testId: DistrictsOrBoroughsSelectorCheckBoxTestId,
        children: cityLabel ? `${cityLabel} - ${allLabel}` : allLabel
      }
    ),
    /* @__PURE__ */ jsx(
      CheckboxGroup,
      {
        marginLeft: "spacing.16",
        label: { content: allLabel, isHidden: true },
        name: `autocomplete-${variant}`,
        value: variantItems.map(({ id }) => id),
        items: checkboxGroupItems,
        onChange: onItemClick,
        testId: DistrictsOrBoroughsSelectorCheckBoxGroupTestId
      }
    )
  ] });
};
var DistrictsAndBoroughsContainer = ({ cityId }) => {
  const [chipTextKey, setChipTextKey] = useState("modals.districts.checkbox_group.all_districts.label");
  const panels = [];
  const districtsLabel = useTranslate("modals.districts.tabs.districts.label");
  const boroughsLabel = useTranslate("modals.districts.tabs.boroughs.label");
  const chipText = useTranslate(chipTextKey);
  const districts = useRelation(cityId, PlaceType.NEIGHBORHOOD);
  if (districts.length > 0) {
    panels.push({
      label: districtsLabel,
      id: "districts",
      content: /* @__PURE__ */ jsx(DistrictsOrBoroughsSelector, { cityId, variant: "districts", relations: districts })
    });
  }
  const boroughs = useRelation(cityId, PlaceType.BOROUGHS);
  if (boroughs.length > 0) {
    panels.push({
      label: boroughsLabel,
      id: "boroughs",
      content: /* @__PURE__ */ jsx(DistrictsOrBoroughsSelector, { cityId, variant: "boroughs", relations: boroughs })
    });
  }
  return /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "column", width: "100%", children: [
    /* @__PURE__ */ jsx(DistrictsAndBoroughsChips, { cityId, defaultChipText: chipText }),
    panels.length === 2 && /* @__PURE__ */ jsx(
      Tabs,
      {
        items: panels,
        hasDivider: true,
        onChange: (id) => setChipTextKey(`modals.districts.checkbox_group.all_${id}.label`)
      }
    ),
    panels.length === 1 && panels[0].content
  ] });
};

// src/helpers/hooks/useSubmitButton.ts
var submitButtonTestId = "search-mfe-modal-submit-button";
var useSubmitButton = (type, isDisabled = false) => {
  const { count, inProgress } = useSearchContext();
  const { submitText } = useModalContext();
  const isLoading = !submitText && !isDisabled && inProgress;
  const text = useTranslate("modals.submitButton.text_interval", {
    postProcess: "interval",
    count
  });
  return {
    children: submitText ?? text,
    type,
    isLoading,
    isDisabled,
    form: RefinerFormDefaultId,
    onPress: () => {
    },
    testId: submitButtonTestId
  };
};
var DistrictsSelectorModalTestId = "searchmfe-districtsselector-modal-testid";
var DistrictsAndBoroughsModal = ({ cityId, ...props }) => {
  const titleLokalized = useTranslate("modals.districts.title.default");
  const submitButton = useSubmitButton("button");
  return /* @__PURE__ */ jsx(
    ModalContainer,
    {
      ...props,
      title: titleLokalized,
      testId: DistrictsSelectorModalTestId,
      footerEndButton: submitButton,
      children: /* @__PURE__ */ jsx(DistrictsAndBoroughsContainer, { cityId })
    }
  );
};
var CityLocationItem = ({ locationIndex, onClose }) => {
  const { control, getValues } = useFormContext();
  const { update } = useFieldArray({ name: "locations", control });
  const location = getValues("locations")?.[locationIndex] ?? {};
  const { id, districts = [], boroughs = [], labels } = location;
  const [savedDistrictsAndBoroughs, setSavedDistrictsAndBoroughs] = useState({ districts, boroughs });
  const adjustDistrictsLabel = useTranslate("location.items.districts.label");
  const headerProps = {
    icon: LocationDotFilledIcon,
    ariaLabel: useTranslate("location.items.remove.aria_label"),
    title: labels?.[0],
    onClose
  };
  const tags = [...districts, ...boroughs].map(({ labels: [label] }) => ({
    label
  }));
  return /* @__PURE__ */ jsx(
    DumbLocationItem,
    {
      headerProps,
      tags,
      modal: () => /* @__PURE__ */ jsx(
        DistrictsAndBoroughsModal,
        {
          trigger: ({ onPress }) => /* @__PURE__ */ jsx(Chip, { variant: "action", onPress, children: adjustDistrictsLabel }),
          cityId: id,
          onOpen: () => setSavedDistrictsAndBoroughs({ districts, boroughs }),
          onCancel: () => update(locationIndex, { ...location, ...savedDistrictsAndBoroughs }),
          onValidate: () => {
          }
        }
      )
    }
  );
};
var DefaultLocationItem = ({ locationIndex, onClose }) => {
  const { getValues } = useFormContext();
  const location = getValues("locations")?.[locationIndex] ?? {};
  const { labels } = location;
  const headerProps = {
    icon: LocationDotFilledIcon,
    ariaLabel: useTranslate("location.items.remove.aria_label"),
    title: labels?.[0],
    onClose
  };
  return /* @__PURE__ */ jsx(DumbLocationItem, { headerProps, tags: [], modal: void 0 });
};
var CommuteDurationDropdown = ({ locationIndex }) => {
  const label = useTranslate("filters.travel_time.box.duration.title");
  const placeholder = useTranslate("filters.travel_time.box.duration.placeholder");
  const { control } = useFormContext();
  const timeSteps = [5, 10, 15, 20, 25, 30, 45, 60];
  const timeItems = timeSteps.map((time) => ({
    id: String(time),
    label: `${time} min`
  }));
  return /* @__PURE__ */ jsx(
    Controller,
    {
      name: `locations.${locationIndex}.commuteDuration`,
      control,
      render: ({ field: { onChange, value } }) => /* @__PURE__ */ jsx(
        Dropdown,
        {
          hasNecessityIndicator: false,
          label,
          placeholder,
          defaultItems: timeItems,
          value: value?.toString(),
          onChange
        }
      )
    }
  );
};
var selectableTravelModes = [
  { icon: CarIcon, value: CommuteMode.CAR },
  { icon: BicycleIcon, value: CommuteMode.BIKE },
  { icon: WalkingIcon, value: CommuteMode.WALK },
  { icon: TramIcon, value: CommuteMode.TRANSIT }
];
var useCommuteModeOptions = () => {
  const title = useTranslate("filters.travel_time.box.means.title");
  const getCommuteText = (mode) => useTranslate(`filters.travel_time.box.means.${mode.toLowerCase()}`);
  const items = selectableTravelModes.map(({ icon, value }) => ({
    value,
    label: upperFirst(value),
    icon
  }));
  return {
    title,
    getCommuteText,
    items
  };
};
var CommuteModeButtonGroup = ({ locationIndex }) => {
  const { title, getCommuteText, items } = useCommuteModeOptions();
  const { control } = useFormContext();
  return /* @__PURE__ */ jsx(
    Controller,
    {
      name: `locations.${locationIndex}.commuteMode`,
      control,
      render: ({ field: { onChange, value, ...rest } }) => /* @__PURE__ */ jsxs(Box, { width: "100%", display: "flex", flexDirection: "column", gap: "spacing.4", children: [
        /* @__PURE__ */ jsxs(Box, { typography: "typography.body.14.bold", display: "flex", justifyContent: "space-between", width: "100%", children: [
          /* @__PURE__ */ jsx(Box, { as: "span", children: title }),
          /* @__PURE__ */ jsx(Box, { as: "span", children: getCommuteText(value ?? CommuteMode.CAR) })
        ] }),
        /* @__PURE__ */ jsx(
          ButtonGroup,
          {
            ...rest,
            value,
            onChange,
            isFullWidth: true,
            items,
            label: "Transportation mean",
            type: "radio",
            iconOnly: true
          }
        )
      ] })
    }
  );
};
var AddressAutocompleteTestId = "searchmfe-address-autocomplete-testid";
var minimumInputLength = 2;
var AddressAutocomplete = ({
  initialAddress = "",
  placeholderText,
  ariaLabelText,
  onSubmit,
  inError,
  onChange = () => {
  }
}) => {
  const [input, setInput] = useState(initialAddress);
  const [items, setItems] = useState([]);
  const serviceUrl = useServiceUrl();
  const errorMessage = useTranslate("filters.travel_time.box.location.error_message");
  const locale = useLocale();
  useDebounce(
    async () => {
      if (input?.length < minimumInputLength) {
        setItems([]);
        return;
      }
      const newItems = await callAutocomplete({
        input,
        serviceUrl,
        locationTypes: [PlaceType.HOUSE_NUMBER, PlaceType.STREETS],
        parentLocationTypes: [PlaceType.STREETS, PlaceType.MUNICIPALITY],
        locale
      });
      setItems(
        newItems.map((item) => ({
          ...item,
          suffix: item.parents?.[0]?.labels?.join("") || ""
        }))
      );
    },
    300,
    [input]
  );
  const handleOnSubmit = (key) => {
    const autocompleteItem = items.find((item) => item.id === key);
    if (autocompleteItem) {
      setInput(autocompleteItem.label ?? "");
      onSubmit(autocompleteItem);
    }
  };
  const handleOnChange = (key) => {
    onChange();
    setInput(key);
  };
  return /* @__PURE__ */ jsx(
    Autocomplete,
    {
      items,
      value: input,
      onSubmit: (_5, key) => handleOnSubmit(key),
      onChange: handleOnChange,
      label: { content: ariaLabelText || "", isHidden: true },
      placeholder: placeholderText,
      testId: AddressAutocompleteTestId,
      error: inError ? errorMessage : void 0
    }
  );
};
var fetchIsochrone = async ({ id, coordinates, commuteMode, commuteDuration }, serviceUrl) => {
  if (!id || isEmpty(coordinates) || !commuteMode || !commuteDuration) {
    return [];
  }
  const { lat, lng } = coordinates;
  const config = {
    params: { id, lat, lng, commuteMode, commuteDuration }
  };
  const url = `${serviceUrl}/routing/isochrone`;
  const result = await axios.get(url, config).catch(() => ({ data: { isochrone: [] } }));
  return result?.data?.isochrone ?? [];
};
var multipolygonToPolylines = (multipolygon) => multipolygon.map((polygon) => encode(polygon[0].map(([lng, lat]) => [lat, lng])));

// src/helpers/utils/polylines/getPolylines.ts
var getPolylines = async (isochroneProps, serviceUrl) => {
  const data = await fetchIsochrone(isochroneProps, serviceUrl);
  return multipolygonToPolylines(data);
};

// src/helpers/hooks/useIsochrone.ts
var useIsochrone = (locationIndex) => {
  const { setIsIsochroneLoading } = useSearchContext();
  const [isLoaded, setIsLoaded] = useState(false);
  const serviceUrl = useServiceUrl();
  const { watch, control } = useFormContext();
  const { update } = useFieldArray({ name: "locations", control });
  const location = watch("locations")?.[locationIndex];
  const updatePolylines = (index, polylines) => {
    update(index, {
      ...location,
      polylines
    });
  };
  useEffect(() => {
    if (!isLoaded) {
      setIsLoaded(true);
      if (location?.polylines && location.polylines.length > 0)
        return;
    }
    if (!location) {
      setIsIsochroneLoading(false);
      return;
    }
    const getData = async () => {
      try {
        if (!location.id || !location.commuteMode || !location.commuteDuration || !location.coordinates) {
          setIsIsochroneLoading(false);
          return;
        }
        setIsIsochroneLoading(true);
        const polylines = await getPolylines(location, serviceUrl);
        updatePolylines(locationIndex, polylines);
      } catch {
        updatePolylines(locationIndex, []);
      } finally {
        setTimeout(() => {
          setIsIsochroneLoading(false);
        }, 300);
      }
    };
    void getData();
  }, [location?.id, location?.commuteMode, location?.commuteDuration]);
};
var CommuteContainer = ({
  locationIndex,
  autocompleteInError,
  onAutocompleteChange
}) => {
  const { control, watch } = useFormContext();
  const { update } = useFieldArray({ name: "locations", control });
  const location = watch("locations")?.[locationIndex];
  const isInit = useInitialCommuteLocation({ locationIndex });
  useIsochrone(locationIndex);
  const locationLabel = useTranslate("filtersBar.title.location");
  const placeholderText = useTranslate("filters.travel_time.box.location.placeholder");
  const addressAutocompleteProps = {
    onSubmit: ({ id, labels, coordinates, parents }) => {
      const cityLabels = parents.find((parent) => parent.type_key === PlaceType.MUNICIPALITY)?.labels ?? [];
      update(locationIndex, {
        ...location,
        id,
        labels: [...labels, ...cityLabels],
        coordinates
      });
    },
    initialAddress: location?.labels?.[0],
    placeholderText,
    ariaLabelText: placeholderText,
    inError: autocompleteInError,
    onChange: onAutocompleteChange
  };
  return isInit ? /* @__PURE__ */ jsxs(
    Box,
    {
      display: "flex",
      flexDirection: "column",
      gap: { "breakpoint.base": "spacing.16", "breakpoint.md": "spacing.40" },
      width: "100%",
      children: [
        /* @__PURE__ */ jsxs(Box, { width: "100%", display: "flex", flexDirection: "column", gap: "spacing.4", children: [
          /* @__PURE__ */ jsx(Box, { typography: "typography.body.14.bold", children: locationLabel }),
          /* @__PURE__ */ jsx(AddressAutocomplete, { ...addressAutocompleteProps })
        ] }),
        /* @__PURE__ */ jsx(CommuteModeButtonGroup, { locationIndex }),
        /* @__PURE__ */ jsx(CommuteDurationDropdown, { locationIndex })
      ]
    }
  ) : void 0;
};
var onCommuteSubmitCheck = (location, setAutocompleteInError) => {
  const isSubmitOk = !!location && !isEmpty(location.id) && !!location.commuteDuration && !!location.commuteMode && !isEmpty(location.polylines);
  setAutocompleteInError(!isSubmitOk);
  return isSubmitOk;
};
var MapModalSubmitButton = ({ onPress, testId }) => {
  const { count, inProgress, isIsochroneLoading, isValid } = useSearchContext();
  const { submitText } = useModalContext();
  const isLoading = inProgress || isIsochroneLoading;
  const showResultsLabel = isValid ? useTranslate("modals.submitButton.text_interval", {
    postProcess: "interval",
    count
  }) : useTranslate("modals.submitButton.placeholder");
  return /* @__PURE__ */ jsx(
    Box,
    {
      paddingHorizontal: "spacing.32",
      paddingVertical: "spacing.16",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      children: /* @__PURE__ */ jsx(Button, { isLoading, isFullWidth: true, variant: "primary", onPress, testId, children: submitText ?? showResultsLabel })
    }
  );
};
var MapModalTitleCancelTestId = "searchmfe-map-modal-title-cancel-testid";
var MapModalTitle = ({ title, onClose }) => /* @__PURE__ */ jsxs(
  Box,
  {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    typography: "typography.body.16.bold",
    padding: "spacing.8",
    gap: "spacing.8",
    children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          icon: CloseIcon,
          size: "32",
          variant: "primary-inverted",
          children: "Close",
          iconOnly: true,
          onPress: onClose,
          testId: MapModalTitleCancelTestId
        }
      ),
      title
    ]
  }
);
var breakpoints = {
  base: "base",
  md: "medium"
};
var useBreakpoint = () => useBreakpointValue(breakpoints);

// src/helpers/hooks/useIsMobile.ts
var useIsMobile = () => useBreakpoint() === "base";
var MapModalResponsiveDesktopBoxTestId = "searchmfe-map-modal-responsive-desktop-box-testid";
var MapModalResponsiveMobileBoxTestId = "searchmfe-map-modal-responsive-mobile-box-testid";
var MobileContainer = ({ title, children, onClose, map, submitButtonProps }) => /* @__PURE__ */ jsxs(Box, { position: "relative", height: "100%", width: "100%", testId: MapModalResponsiveMobileBoxTestId, children: [
  /* @__PURE__ */ jsx(MapModalTitle, { onClose, title }),
  /* @__PURE__ */ jsx(Box, { width: "100%", height: "320px", children: map }),
  /* @__PURE__ */ jsx(Box, { width: "100%", height: "fit-content", padding: "spacing.16", children }),
  /* @__PURE__ */ jsxs(Box, { position: "relative", display: "flex", flexDirection: "column", alignContent: "center", children: [
    /* @__PURE__ */ jsx(Divider, {}),
    /* @__PURE__ */ jsx(MapModalSubmitButton, { ...submitButtonProps })
  ] })
] });
var DesktopContainer = ({ title, children, onClose, map, submitButtonProps }) => /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "row", width: "100%", height: "100%", testId: MapModalResponsiveDesktopBoxTestId, children: [
  /* @__PURE__ */ jsxs(Box, { display: "flex", width: "378px", height: "100%", flexDirection: "column", justifyContent: "space-between", children: [
    /* @__PURE__ */ jsxs(Box, { width: "100%", children: [
      /* @__PURE__ */ jsx(MapModalTitle, { onClose, title }),
      /* @__PURE__ */ jsx(Box, { width: "100%", display: "flex", padding: "spacing.32", children })
    ] }),
    /* @__PURE__ */ jsxs(Box, { position: "relative", display: "flex", flexDirection: "column", alignContent: "center", children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(MapModalSubmitButton, { ...submitButtonProps })
    ] })
  ] }),
  /* @__PURE__ */ jsx(Box, { width: "calc(100% - 378px)", height: "100%", position: "relative", children: map })
] });
var MapModalResponsiveBox = (props) => {
  const isMobile = useIsMobile();
  return isMobile ? /* @__PURE__ */ jsx(MobileContainer, { ...props }) : /* @__PURE__ */ jsx(DesktopContainer, { ...props });
};
var MapModalContainer = ({
  title,
  trigger,
  children,
  map,
  modalTestId,
  submitButtonTestId: submitButtonTestId2,
  onValidate = () => {
  },
  onSubmitCheck = () => true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const displayMode = isMobile ? "fullscreen" : "fullHeight";
  const { getValues, resetField } = useFormContext();
  const [savedLocations, setSavedLocations] = useState(() => getValues().locations);
  const open = () => {
    setSavedLocations(getValues().locations);
    setIsOpen(true);
  };
  const cancelAndclose = () => {
    resetField("locations", { defaultValue: savedLocations });
    setIsOpen(false);
  };
  const validateAndclose = () => {
    onValidate();
    setSavedLocations(getValues().locations);
    setIsOpen(false);
  };
  return /* @__PURE__ */ jsx(
    Modal,
    {
      testId: modalTestId,
      trigger,
      isDismissable: true,
      hasCloseButton: false,
      hasContentPadding: false,
      title: void 0,
      isOpen,
      displayMode,
      predefinedWidth: "1120",
      onOpenChange: (changedIsOpen) => {
        if (changedIsOpen)
          open();
        else
          cancelAndclose();
      },
      children: /* @__PURE__ */ jsx(
        MapModalResponsiveBox,
        {
          submitButtonProps: {
            testId: submitButtonTestId2,
            onPress: () => {
              if (onSubmitCheck())
                validateAndclose();
            }
          },
          onClose: cancelAndclose,
          title,
          map,
          children
        }
      )
    }
  );
};
var CommuteModalTestId = "searchmfe-commute-modal-testid";
var SubmitCommuteModalTestId = "searchmfe-submit-commute-modal-testid";
var LazyCommuteMap = React.lazy(
  () => import('./CommuteMap-WDJTO7QF.js').then((module) => ({
    default: module.CommuteMap
  }))
);
var CommuteModal = ({ locationIndex, trigger, onValidate }) => {
  const { getValues } = useFormContext();
  const localisedTitle = useTranslate("filters.travel_time.button.label");
  const [autocompleteInError, setAutocompleteInError] = useState(false);
  const { isIsochroneLoading } = useSearchContext();
  return /* @__PURE__ */ jsx(
    MapModalContainer,
    {
      map: /* @__PURE__ */ jsx(Suspense, { children: /* @__PURE__ */ jsx(LazyCommuteMap, { locationIndex }) }),
      modalTestId: CommuteModalTestId,
      submitButtonTestId: SubmitCommuteModalTestId,
      title: localisedTitle,
      onSubmitCheck: () => !isIsochroneLoading && onCommuteSubmitCheck(getValues("locations")?.[locationIndex], setAutocompleteInError),
      onValidate,
      trigger,
      children: /* @__PURE__ */ jsx(
        CommuteContainer,
        {
          locationIndex,
          autocompleteInError,
          onAutocompleteChange: () => setAutocompleteInError(false)
        }
      )
    }
  );
};
var UpdateCommuteTriggerTestId = "searchmfe-update-commute-trigger-testid";
var commuteModeIconsRecord = {
  Bike: BicycleIcon,
  Car: CarIcon,
  Transit: BusIcon,
  Walk: WalkingIcon
};
var CommuteLocationItem = ({ locationIndex, onClose }) => {
  const { getValues } = useFormContext();
  const location = getValues("locations")?.[locationIndex];
  if (location?.type !== FormLocationType.COMMUTE)
    return null;
  const { labels, commuteDuration = 0, commuteMode = CommuteMode.BIKE } = location;
  const ctaLabel = useTranslate("location.items.travel_time.label");
  const headerProps = {
    title: useTranslate("location.items.travel_time.title"),
    icon: TramFilledIcon,
    onClose,
    ariaLabel: useTranslate("location.items.travel_time.discard_aria_label")
  };
  const tags = [
    {
      label: labels.join(", "),
      icon: LocationDotIcon
    },
    {
      label: useTranslate(`filters.travel_time.box.means.${commuteMode.toLowerCase()}`),
      icon: commuteModeIconsRecord[commuteMode]
    },
    {
      label: `${commuteDuration} min`,
      icon: ClockIcon
    }
  ];
  return /* @__PURE__ */ jsx(
    DumbLocationItem,
    {
      headerProps,
      tags,
      modal: () => /* @__PURE__ */ jsx(
        CommuteModal,
        {
          locationIndex,
          trigger: ({ onPress }) => /* @__PURE__ */ jsx(Chip, { testId: UpdateCommuteTriggerTestId, variant: "action", onPress, children: ctaLabel })
        }
      )
    }
  );
};
var defaultRadius = 10;
var getInitialLocationData = (location, defaultPlace) => {
  const districts = location?.districts ?? [];
  const boroughs = location?.boroughs ?? [];
  const combinedAreas = [...districts, ...boroughs];
  const { id, labels, postalCodes, coordinates } = combinedAreas[0] ?? location ?? {};
  const updatedId = location?.type === FormLocationType.COMMUTE ? "" : id;
  const updatedLabels = location?.type === FormLocationType.COMMUTE ? [""] : labels;
  return {
    coordinates: coordinates ?? defaultPlace.coordinates,
    id: updatedId,
    labels: updatedLabels,
    postalCodes: postalCodes ?? []
  };
};
var useInitialRadiusLocation = ({ locationIndex }) => {
  const { getValues, setValue } = useFormContext();
  const defaultLocation = useDefaultLocation();
  const [isInit, setIsInit] = useState(false);
  useEffect(() => {
    const location = getValues("locations")?.[locationIndex];
    if (location?.type === FormLocationType.RADIUS) {
      setIsInit(true);
      return;
    }
    const data = getInitialLocationData(location, defaultLocation);
    setValue("locations", [
      {
        ...data,
        parentIds: [],
        radius: defaultRadius,
        type: FormLocationType.RADIUS
      }
    ]);
    setIsInit(true);
  }, []);
  return isInit;
};
var getPolyline = ({ radius, lat, lng }) => {
  try {
    const radiusInMeter = radius * 1e3;
    const {
      coordinates: [firstCoordinate]
    } = circleToPolygon([lng, lat], radiusInMeter, 32);
    if (!firstCoordinate || firstCoordinate.length === 0)
      return void 0;
    return encode(firstCoordinate.map(([longitude, latitude]) => [latitude, longitude]));
  } catch {
    return void 0;
  }
};

// src/containers/RadiusModal/hooks/useRadius.ts
var useRadius = (locationIndex) => {
  const { watch, control } = useFormContext();
  const { update } = useFieldArray({ name: "locations", control });
  const location = watch("locations")?.[locationIndex];
  useEffect(() => {
    if (!location || !location.id || !location.radius || !location.coordinates) {
      return;
    }
    const polyline = getPolyline({
      radius: location.radius,
      lat: location.coordinates.lat,
      lng: location.coordinates.lng
    });
    if (polyline) {
      update(locationIndex, {
        ...location,
        polyline
      });
    }
  }, [location?.id, location?.radius]);
};
var selectableRadius = [3, 5, 10, 15, 20, 30, 50];
var RadiusButtonGroup = () => {
  const { control, getValues } = useFormContext();
  const { update } = useFieldArray({ name: "locations", control });
  const items = selectableRadius.map((value) => ({ value: `${value}`, label: `${value}` }));
  const locationIndex = 0;
  return /* @__PURE__ */ jsx(
    Controller,
    {
      name: `locations.${locationIndex}.radius`,
      control,
      render: ({ field: { onChange, value, ...rest } }) => {
        const radiusOnChange = (newRadiusValue) => {
          const radius = parseInt(newRadiusValue, 10);
          const currentLocation = getValues("locations")[locationIndex];
          const { coordinates } = currentLocation;
          if (coordinates !== void 0) {
            update(locationIndex, {
              ...currentLocation,
              type: FormLocationType.RADIUS,
              radius,
              polyline: getPolyline({ radius, ...coordinates })
            });
            onChange(radius);
          }
        };
        return /* @__PURE__ */ jsx(
          ButtonGroup,
          {
            ...rest,
            value: value && value > 0 ? `${value}` : void 0,
            onChange: radiusOnChange,
            isFullWidth: true,
            marginVertical: "spacing.4",
            items,
            label: "radius",
            type: "radio"
          }
        );
      }
    }
  );
};
var RadiusContainer = ({ locationIndex, autocompleteInError, onAutocompleteChange }) => {
  const { control, watch } = useFormContext();
  const { update } = useFieldArray({ name: "locations", control });
  const location = watch("locations")?.[locationIndex];
  const isInit = useInitialRadiusLocation({ locationIndex });
  useRadius(locationIndex);
  const locationLabel = useTranslate("filtersBar.title.location");
  const radiusSelectorLabel = useTranslate("filters.radius.buttongroup.label");
  const placeholderText = useTranslate("location.autocomplete.textfield.placeholder.empty");
  const ariaLabelText = useTranslate("location.autocomplete.textfield.aria_label");
  const locationAutocompleteProps = {
    onSubmit: ({ id, labels, coordinates }) => {
      update(locationIndex, {
        ...location,
        id,
        labels,
        coordinates
      });
    },
    errorMessage: useTranslate("filters.radius.box.location.error_message"),
    initialInput: location?.labels?.[0],
    placeholderText,
    ariaLabelText,
    inError: autocompleteInError,
    onChange: onAutocompleteChange
  };
  return isInit ? /* @__PURE__ */ jsxs(
    Box,
    {
      display: "flex",
      flexDirection: "column",
      rowGap: { "breakpoint.base": "spacing.16", "breakpoint.md": "spacing.40" },
      children: [
        /* @__PURE__ */ jsxs(Box, { width: "100%", display: "flex", flexDirection: "column", rowGap: "spacing.4", children: [
          /* @__PURE__ */ jsx(Box, { typography: "typography.body.14.bold", children: locationLabel }),
          /* @__PURE__ */ jsx(LocationAutocomplete, { ...locationAutocompleteProps })
        ] }),
        /* @__PURE__ */ jsxs(Box, { width: "100%", display: "flex", flexDirection: "column", rowGap: "spacing.4", children: [
          /* @__PURE__ */ jsx(Box, { typography: "typography.body.14.bold", children: radiusSelectorLabel }),
          /* @__PURE__ */ jsx(RadiusButtonGroup, {})
        ] })
      ]
    }
  ) : void 0;
};
var onRadiusSubmitCheck = (location, setAutocompleteInError) => {
  const isSubmitOk = !!location && !isEmpty(location.id) && !!location.radius && !!location.polyline;
  setAutocompleteInError(!isSubmitOk);
  return isSubmitOk;
};
var RadiusModalTestId = "searchmfe-radius-modal-testid";
var SubmitRadiusModalTestId = "searchmfe-submit-radius-modal-testid";
var LazyRadiusMap = React.lazy(
  () => import('./RadiusMap-F56LHE4H.js').then((module) => ({
    default: module.RadiusMap
  }))
);
var RadiusModal = ({ locationIndex, onValidate, trigger }) => {
  const { getValues } = useFormContext();
  const localisedTitle = useTranslate("modals.radius.title.default");
  const [autocompleteInError, setAutocompleteInError] = useState(false);
  return /* @__PURE__ */ jsx(
    MapModalContainer,
    {
      map: /* @__PURE__ */ jsx(Suspense, { children: /* @__PURE__ */ jsx(LazyRadiusMap, { locationIndex }) }),
      modalTestId: RadiusModalTestId,
      submitButtonTestId: SubmitRadiusModalTestId,
      title: localisedTitle,
      onSubmitCheck: () => onRadiusSubmitCheck(getValues().locations[locationIndex], setAutocompleteInError),
      onValidate,
      trigger,
      children: /* @__PURE__ */ jsx(
        RadiusContainer,
        {
          locationIndex,
          autocompleteInError,
          onAutocompleteChange: () => setAutocompleteInError(false)
        }
      )
    }
  );
};
var RadiusUpdateTestId = "searchmfe-radius-location-update-testid";
var RadiusLocationItem = ({ locationIndex, onClose }) => {
  const { getValues } = useFormContext();
  const { labels, radius } = getValues().locations[locationIndex];
  const adjustRadiusLabel = useTranslate("filters.radius.selected.button.label");
  const headerProps = {
    title: useTranslate("filters.radius.selected.title"),
    ariaLabel: useTranslate("location.items.remove.aria_label"),
    icon: LocationCircleFilledIcon,
    onClose
  };
  const tags = [
    {
      label: labels?.[0],
      icon: LocationDotIcon
    },
    {
      label: `+ ${radius}km`
    }
  ];
  return /* @__PURE__ */ jsx(
    DumbLocationItem,
    {
      headerProps,
      tags,
      modal: () => /* @__PURE__ */ jsx(
        RadiusModal,
        {
          locationIndex,
          trigger: ({ onPress }) => /* @__PURE__ */ jsx(Chip, { testId: RadiusUpdateTestId, variant: "action", onPress, children: adjustRadiusLabel })
        }
      )
    }
  );
};
var AutocompleteLocationsTestId = "searchmfe-ac-locations-testid";
var LocationsWrapper = ({ locations, onChange }) => /* @__PURE__ */ jsx(Box, { width: "100%", testId: AutocompleteLocationsTestId, display: "flex", flexDirection: "column", rowGap: "spacing.24", children: locations.map(({ id: currentId, type }, index) => {
  const locationItemProps = {
    locationIndex: index,
    onClose: () => onChange(locations.filter(({ id: thisId }) => thisId !== currentId))
  };
  return /* @__PURE__ */ jsxs(Box, { children: [
    type === FormLocationType.ID && /* @__PURE__ */ jsx(DefaultLocationItem, { ...locationItemProps }),
    type === FormLocationType.CITY && /* @__PURE__ */ jsx(CityLocationItem, { ...locationItemProps }),
    type === FormLocationType.COMMUTE && /* @__PURE__ */ jsx(CommuteLocationItem, { ...locationItemProps }),
    type === FormLocationType.RADIUS && /* @__PURE__ */ jsx(RadiusLocationItem, { ...locationItemProps })
  ] }, index);
}).reverse() });
var BaseAlternateSearchTriggerButton = ({ onPress, title, icon, testId }, forwardedRef) => /* @__PURE__ */ jsx(Box, { ref: forwardedRef, width: "100%", children: /* @__PURE__ */ jsx(
  Box,
  {
    borderColor: "color.border.light.default",
    borderWidth: "borderWidth.1",
    borderRadius: "radius.8",
    borderStyle: "solid",
    width: "100%",
    children: /* @__PURE__ */ jsx(
      CellContent,
      {
        title,
        padding: "16",
        onPress,
        placeholderStart: icon,
        iconEnd: ChevronRightIcon,
        testId
      }
    )
  }
) });
var AlternateSearchTriggerButton = forwardRef(
  BaseAlternateSearchTriggerButton
);

// src/criteria/CommuteFilter/useCommuteFilter.ts
var useCommuteFilter = () => {
  const { showCommuteFilter } = useConditionalFilter();
  return showCommuteFilter;
};
var AlternateSearchesTestId = "searchmfe-alternate-searches-testid";
var RadiusTriggerButtonTestId = "searchmfe-radius-trigger-button-testid";
var CommuteTriggerButtonTestId = "searchmfe-commute-trigger-button-testid";
var AlternateSearches = ({ locations, onValidate }) => {
  const title = useTranslate("filters.radius.button.title");
  const showCommuteFilter = useCommuteFilter();
  const radiusButtonProps = {
    title: useTranslate("filters.radius.button.label"),
    icon: LocationCircleIcon,
    testId: RadiusTriggerButtonTestId
  };
  const commuteButtonProps = {
    title: useTranslate("filters.travel_time.button.label"),
    icon: TramIcon,
    testId: CommuteTriggerButtonTestId
  };
  const displayCommuteSelector = showCommuteFilter && !locations.some(({ type }) => type === FormLocationType.COMMUTE);
  const displayRadiusSelector = !locations.some(({ type }) => type === FormLocationType.RADIUS);
  return displayCommuteSelector || displayRadiusSelector ? /* @__PURE__ */ jsxs(Box, { width: "100%", testId: AlternateSearchesTestId, children: [
    /* @__PURE__ */ jsx(Box, { textAlign: "left", typography: "typography.body.14.regular", marginBottom: "spacing.8", children: title }),
    /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "column", gap: "spacing.8", children: [
      displayRadiusSelector && /* @__PURE__ */ jsx(
        RadiusModal,
        {
          locationIndex: 0,
          onValidate,
          trigger: ({ onPress }) => /* @__PURE__ */ jsx(AlternateSearchTriggerButton, { onPress, ...radiusButtonProps })
        }
      ),
      displayCommuteSelector && /* @__PURE__ */ jsx(
        CommuteModal,
        {
          locationIndex: 0,
          onValidate,
          trigger: ({ onPress }) => /* @__PURE__ */ jsx(AlternateSearchTriggerButton, { onPress, ...commuteButtonProps })
        }
      )
    ] })
  ] }) : null;
};
var BaseMultiLocationAutocomplete = ({ locations = [], onChange }) => {
  const { getValues } = useFormContext();
  const [savedLocations, setSavedLocations] = useState(() => getValues("locations"));
  const onLocationsChange = (newLocations) => {
    onChange(newLocations);
    setSavedLocations(newLocations);
  };
  const enableAutocomplete = locations.every(
    ({ type }) => type === FormLocationType.ID || type === FormLocationType.CITY
  );
  return /* @__PURE__ */ jsxs(
    Box,
    {
      display: "flex",
      alignItems: "flex-start",
      flexDirection: "column",
      gap: "spacing.24",
      typography: "typography.body.16.bold",
      children: [
        /* @__PURE__ */ jsx(Box, { width: "100%", children: /* @__PURE__ */ jsx(
          LocationAutocompleteWrapper,
          {
            isDisabled: !enableAutocomplete,
            locations,
            onChange: onLocationsChange
          }
        ) }),
        /* @__PURE__ */ jsx(LocationsWrapper, { locations, onChange: onLocationsChange }),
        /* @__PURE__ */ jsx(AlternateSearches, { locations: savedLocations, onValidate: () => setSavedLocations(getValues().locations) })
      ]
    }
  );
};
var MultiLocationAutocomplete = () => {
  const { control } = useFormContext();
  return /* @__PURE__ */ jsx(
    Controller,
    {
      control,
      name: "locations",
      render: ({ field: { onChange, value } }) => /* @__PURE__ */ jsx(BaseMultiLocationAutocomplete, { locations: value, onChange })
    }
  );
};
var MultiLocationAutocompleteModalTestId = "searchmfe-mlautocomplete-modal-testid";
var MultiLocationAutocompleteButtonTestId = "searchmfe-mlautocomplete-button-testid";
var BaseMultiLocationAutocompleteModal = ({
  modalProps,
  isShortcut = false,
  savedParentModel
}) => {
  const titleLokalized = useTranslate("modals.location.title.default");
  const filterButtonCommuteLabel = useTranslate("filtersBar.title.location_commute");
  const filterButtonAriaLabel = useTranslate("filtersBar.title.location");
  const [title, setTitle] = useState(
    getLocationTitle({ formModel: savedParentModel, customTitles: { commuteLabel: filterButtonCommuteLabel } })
  );
  const filterButtonProps = {
    icon: LocationDotIcon,
    value: title,
    label: "-",
    ariaLabel: filterButtonAriaLabel
  };
  useEffect(() => {
    setTitle(
      getLocationTitle({ formModel: savedParentModel, customTitles: { commuteLabel: filterButtonCommuteLabel } })
    );
  }, [savedParentModel]);
  return /* @__PURE__ */ jsx(
    ModalContainer,
    {
      title: titleLokalized,
      trigger: (props) => /* @__PURE__ */ jsx(
        FilterButton,
        {
          ...filterButtonProps,
          ...props,
          isShortcut,
          testId: MultiLocationAutocompleteButtonTestId
        }
      ),
      testId: MultiLocationAutocompleteModalTestId,
      ...modalProps,
      children: /* @__PURE__ */ jsx(MultiLocationAutocomplete, {})
    }
  );
};
var MultiLocationAutocompleteModal = ({ savedModel, setSavedModel }) => {
  const { getValues, reset } = useFormContext();
  const isDisabled = isCountButtonDisabled(getValues("locations"));
  const submitButton = useSubmitButton("submit", isDisabled);
  const modalProps = {
    onOpen: () => setSavedModel(getValues()),
    onCancel: () => reset(savedModel),
    onValidate: () => setSavedModel(getValues()),
    footerEndButton: submitButton
  };
  return /* @__PURE__ */ jsx(Box, { minWidth: 0, width: "100%", children: /* @__PURE__ */ jsx(
    BaseMultiLocationAutocompleteModal,
    {
      modalProps,
      savedParentModel: savedModel,
      setSavedParentModel: setSavedModel
    }
  ) });
};

// src/constants/staticAssets.ts
var staticAssetsPath = "/assets/mfes/search_mfe/v1/public";
var BACKGROUND_URL = `${staticAssetsPath}/images/multi-location-modal.png`;
var MultiLocationModalShortcut = () => {
  const { getValues, setValue } = useFormContext();
  const [savedLocations, setSavedLocations] = useState(getValues("locations"));
  const [savedParentModel, setSavedParentModel] = useState(getValues());
  const isDisabled = isCountButtonDisabled(getValues("locations"));
  const submitButton = useSubmitButton("button", isDisabled);
  const modalProps = {
    onOpen: () => setSavedLocations(getValues("locations")),
    onCancel: () => setValue("locations", savedLocations),
    onValidate: () => {
      const newModel = getValues();
      setSavedLocations(newModel.locations);
      setSavedParentModel(newModel);
    },
    footerEndButton: submitButton
  };
  return /* @__PURE__ */ jsx(
    Box,
    {
      backgroundImage: `url(${BACKGROUND_URL})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      paddingVertical: "spacing.24",
      paddingHorizontal: { "breakpoint.base": "spacing.16", "breakpoint.lg": "spacing.32" },
      children: /* @__PURE__ */ jsx(
        BaseMultiLocationAutocompleteModal,
        {
          modalProps,
          isShortcut: true,
          savedParentModel,
          setSavedParentModel
        }
      )
    }
  );
};
var MinMaxDropdown = ({ criteria, type, label, options, testId }) => {
  const { control } = useFormContext();
  return /* @__PURE__ */ jsx(
    Controller,
    {
      name: `${criteria}.${type}`,
      control,
      render: ({ field: { onChange, value, name } }) => /* @__PURE__ */ jsx(
        Dropdown,
        {
          name,
          value: value !== void 0 ? String(value) : "",
          onChange,
          isFullWidth: true,
          hasNecessityIndicator: false,
          label,
          defaultItems: options,
          testId
        }
      )
    }
  );
};
var MinMaxDropdownRangeTestId = "searchmfe-minmaxdropdownrange";
var MinDropdownRangeTestId = "searchmfe-mindropdownrange";
var MaxDropdownRangeTestId = "searchmfe-maxdropdownrange";
var MinMaxDropdownRange = ({ criteria, labels, minOptions, maxOptions }) => /* @__PURE__ */ jsxs(Box, { display: "flex", width: "100%", flexDirection: "row", testId: `${MinMaxDropdownRangeTestId}-${criteria}`, children: [
  /* @__PURE__ */ jsx(Box, { width: "50%", paddingRight: "spacing.8", children: /* @__PURE__ */ jsx(
    MinMaxDropdown,
    {
      criteria,
      type: "min",
      label: labels.min,
      options: minOptions,
      testId: `${MinDropdownRangeTestId}-${criteria}`
    }
  ) }),
  /* @__PURE__ */ jsx(Box, { width: "50%", paddingLeft: "spacing.8", children: /* @__PURE__ */ jsx(
    MinMaxDropdown,
    {
      criteria,
      type: "max",
      label: labels.max,
      options: maxOptions,
      testId: `${MaxDropdownRangeTestId}-${criteria}`
    }
  ) })
] });
var Title = ({ criteriaTitle }) => /* @__PURE__ */ jsx(
  Box,
  {
    testId: `criteria-title-${_2.kebabCase(criteriaTitle)}`,
    marginBottom: "spacing.12",
    typography: "typography.headline.22.bold",
    children: criteriaTitle
  }
);
var BedroomsRangeTestId = "searchmfe-bedroomsrange-testid";
var BedroomsRange = () => {
  const bedroomsTitle = useTranslate("filters.bedrooms_range.title");
  const bedroomsRangeMinLabel = useTranslate("filters.common.range.label.min");
  const bedroomsRangeMaxLabel = useTranslate("filters.common.range.label.max");
  const bedroomsAnyLabel = useTranslate("filters.rooms_range.dropdown.any");
  const { watch } = useFormContext();
  const bedroomsOptionsTemplate = useTemplates().filters.bedrooms;
  const bedRoomsOptions = bedroomsOptionsTemplate?.filter((roomsType) => ROOMS_DICTIONARY[roomsType]).map((roomsType) => ({
    id: roomsType,
    label: ROOMS_DICTIONARY[roomsType].label
  }));
  const bedroomsMin = watch("bedrooms.min");
  const bedroomsMax = watch("bedrooms.max");
  const [minOptions, setMinOptions] = useState(bedRoomsOptions);
  const [maxOptions, setMaxOptions] = useState(bedRoomsOptions);
  const anyOption = { id: RoomsType.ANY, label: bedroomsAnyLabel };
  const filterOptions = (selectedValue, comparator, setOptions) => {
    const filterOption = (option) => comparator(ROOMS_DICTIONARY[option.id].value, ROOMS_DICTIONARY[selectedValue].value);
    const filteredOptions = selectedValue !== RoomsType.ANY ? bedRoomsOptions.filter((option) => filterOption(option)) : [...bedRoomsOptions];
    filteredOptions.unshift(anyOption);
    setOptions(filteredOptions);
  };
  useEffect(() => {
    filterOptions(bedroomsMax, _2.lte, setMinOptions);
    filterOptions(bedroomsMin, _2.gte, setMaxOptions);
  }, [bedroomsMin, bedroomsMax]);
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: BedroomsRangeTestId, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: bedroomsTitle }),
    /* @__PURE__ */ jsx(
      MinMaxDropdownRange,
      {
        criteria: "bedrooms",
        labels: { min: bedroomsRangeMinLabel, max: bedroomsRangeMaxLabel },
        minOptions,
        maxOptions
      }
    )
  ] });
};

// src/criteria/Bedrooms/useBedroomsRange.ts
var useBedroomsRange = () => {
  const { showBedroomsFilter } = useConditionalFilter();
  return showBedroomsFilter;
};
var TextFieldTestIdBase = "searchmfe-textfield-testid";
var BaseMinMaxTextField = ({
  icon,
  suffix,
  label,
  value,
  range,
  errorMessage,
  onChange,
  onBlur,
  testId
}) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.addEventListener("blur", onBlur);
    return () => {
      ref.current?.removeEventListener("blur", onBlur);
    };
  }, []);
  return /* @__PURE__ */ jsx(
    TextField,
    {
      ref,
      error: errorMessage,
      type: "number",
      hasNecessityIndicator: false,
      value: `${value ?? ""}`,
      onChange: (newValue) => {
        if (!newValue) {
          onChange(null);
          return;
        }
        const parsedValue = parseInt(newValue, 10);
        if (parsedValue >= range.min && parsedValue <= range.max)
          onChange(parsedValue);
      },
      label,
      iconEnd: icon,
      isFullWidth: true,
      suffix,
      inputMode: "numeric",
      testId
    }
  );
};
var MinMaxTextField = ({ criteria, type, icon, suffix, label, range, errorMessage }) => {
  const { control, setValue, getValues } = useFormContext();
  return /* @__PURE__ */ jsx(
    Controller,
    {
      name: `${criteria}.${type}`,
      control,
      rules: {
        validate: {
          check: (value) => {
            const min = type === "min" ? value : getValues(`${criteria}.min`);
            const max = type === "max" ? value : getValues(`${criteria}.max`);
            if (max && min && min > max)
              return errorMessage;
            return true;
          }
        }
      },
      render: ({ field: { onChange, onBlur, value }, fieldState: { error, invalid }, formState: { isValidating } }) => {
        useEffect(() => {
          if (!isValidating && invalid)
            setValue(`${criteria}.${type}`, null);
        }, [invalid, isValidating]);
        return /* @__PURE__ */ jsx(
          BaseMinMaxTextField,
          {
            errorMessage: !value ? error?.message : void 0,
            value,
            onChange,
            onBlur,
            label,
            icon,
            range,
            testId: `${TextFieldTestIdBase}-${criteria}-${type}`,
            suffix
          }
        );
      }
    }
  );
};
var MinMaxRangeTestId = "searchmfe-minmaxrange-testid";
var MinMaxRange = ({ ...restProps }) => {
  const rangeMin = useTranslate("filters.common.range.label.min");
  const rangeMax = useTranslate("filters.common.range.label.max");
  const minError = useTranslate("filters.common.range.input_field.error_message.min");
  const maxError = useTranslate("filters.common.range.input_field.error_message.max");
  return /* @__PURE__ */ jsxs(Box, { display: "flex", width: "100%", flexDirection: "row", testId: MinMaxRangeTestId, children: [
    /* @__PURE__ */ jsx(Box, { width: "50%", paddingRight: "spacing.8", children: /* @__PURE__ */ jsx(MinMaxTextField, { type: "min", label: rangeMin, errorMessage: minError, ...restProps }) }),
    /* @__PURE__ */ jsx(Box, { width: "50%", paddingLeft: "spacing.8", children: /* @__PURE__ */ jsx(MinMaxTextField, { type: "max", label: rangeMax, errorMessage: maxError, ...restProps }) })
  ] });
};
var ConstructionYearRangeTestId = "searchmfe-constructionYearrange-testid";
var ConstructionYearRange = () => {
  const constructionYearTitle = useTranslate("filters.construction_year.title");
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: ConstructionYearRangeTestId, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: constructionYearTitle }),
    /* @__PURE__ */ jsx(MinMaxRange, { criteria: "yearOfConstruction", range: { min: 0, max: 999999999 } })
  ] });
};

// src/criteria/ConstructionYear/useConstructionYearRange.ts
var useConstructionYearRange = () => {
  const { showYearofConstructionFilter } = useConditionalFilter();
  return showYearofConstructionFilter;
};
var DistributionTypeTestId = "searchmfe-distributiontype-testid";
var DistributionTypeButtonGroupBase = (_props, forwardedRef) => {
  const { control } = useFormContext();
  const { distributionTypes } = useConditionalFilter();
  const items = distributionTypes.map((value) => ({
    value,
    label: useTranslate(`filters.distribution_type.button_group.${value.toLowerCase()}`)
  }));
  return /* @__PURE__ */ jsx(Box, { ref: forwardedRef, children: /* @__PURE__ */ jsx(
    Controller,
    {
      name: "distributionType",
      control,
      render: ({ field }) => /* @__PURE__ */ jsx(ButtonGroup, { ...field, testId: DistributionTypeTestId, items, label: "distributionType", type: "radio" })
    }
  ) });
};
var DistributionTypeButtonGroup = forwardRef(DistributionTypeButtonGroupBase);

// src/criteria/DistributionType/useDistributionTypeButtonGroupFilter.ts
var useDistributionTypeButtonGroupFilter = () => {
  const { distributionTypes } = useConditionalFilter();
  return distributionTypes.length > 1;
};
var ControlledCheckboxGroup = (props) => {
  const { control } = useFormContext();
  return /* @__PURE__ */ jsx(
    Controller,
    {
      name: props.name,
      control,
      render: ({ field }) => /* @__PURE__ */ jsx(CheckboxGroup, { ...field, ...props, hasNecessityIndicator: false })
    }
  );
};
var highContrastColors = [
  "color.scales.energy.green100",
  "color.scales.energy.green200",
  "color.scales.energy.red100",
  "color.scales.energy.red200",
  "color.scales.energy.red300"
];
var EnergyCertificateBar = ({
  barLabel,
  size,
  value,
  color,
  onChange,
  maxSize
}) => {
  const { "sizing.72": barBaseWidth, "spacing.8": barArrowBaseWidth, "spacing.16": barArrowWidth } = useTokens();
  const scalingForMobile = useBreakpoint() === "base" ? 1 : 2;
  const barIncrease = 1 - size / maxSize / scalingForMobile;
  return /* @__PURE__ */ jsx(
    Box,
    {
      marginLeft: "spacing.8",
      minWidth: barBaseWidth,
      marginRight: `calc((100% - ${barBaseWidth}) * ${barIncrease})`,
      paddingVertical: "spacing.8",
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          display: "flex",
          flexDirection: "row",
          cursor: "pointer",
          onClick: () => onChange(value),
          borderTopLeftRadius: "radius.4",
          borderBottomLeftRadius: "radius.4",
          overflow: "hidden",
          children: [
            /* @__PURE__ */ jsx(
              Box,
              {
                flexGrow: 1,
                backgroundColor: color,
                padding: "spacing.4",
                paddingLeft: "spacing.8",
                color: `color.content.constant.${highContrastColors.includes(color) ? "white" : "black"}`,
                children: barLabel
              }
            ),
            /* @__PURE__ */ jsx(
              Box,
              {
                flexGrow: 0,
                borderTopColor: "color.surface.transparent.default",
                borderLeftColor: color,
                borderBottomColor: "color.surface.transparent.default",
                borderTopStyle: "solid",
                borderLeftStyle: "solid",
                borderBottomStyle: "solid",
                unsafe_style: {
                  borderTopWidth: barArrowWidth,
                  borderLeftWidth: barArrowBaseWidth,
                  borderBottomWidth: barArrowWidth
                }
              }
            )
          ]
        }
      )
    }
  );
};
var addOrRemove = (items, item) => {
  const index = items.indexOf(item);
  if (index >= 0) {
    items.splice(index, 1);
    return items;
  }
  return [...items, item];
};
var EnergyCertificateTestId = "searchmfe-energy-certificate-testid";
var EnergyCertificate = () => {
  const { control, getValues } = useFormContext();
  const title = useTranslate("filters.energy_performance.title");
  const subTitle = useTranslate("filters.energy_performance.subtitle");
  const { energyCertificateClasses } = useTemplates().filters;
  const maxSize = maxBy(energyCertificateClasses, "size").size;
  return /* @__PURE__ */ jsxs(Box, { overflow: "hidden", children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: title }),
    /* @__PURE__ */ jsx(Box, { as: "p", marginVertical: "spacing.12", children: subTitle }),
    /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "row", children: [
      /* @__PURE__ */ jsx(Box, { testId: EnergyCertificateTestId, flexGrow: 0, children: /* @__PURE__ */ jsx(
        ControlledCheckboxGroup,
        {
          label: { content: "energy performance", isHidden: true },
          name: "energyCertificate",
          items: energyCertificateClasses.map(({ value, label }) => ({
            label: label || value,
            value
          }))
        }
      ) }),
      /* @__PURE__ */ jsx(
        Controller,
        {
          name: "energyCertificate",
          control,
          render: ({ field: { onChange, ...field } }) => /* @__PURE__ */ jsx(Box, { ...field, flexGrow: 1, children: energyCertificateClasses.map((props) => /* @__PURE__ */ jsx(
            EnergyCertificateBar,
            {
              ...props,
              onChange: (value) => onChange(addOrRemove(getValues("energyCertificate") || [], value)),
              maxSize
            },
            `energy-bar-${props.value}`
          )) })
        }
      )
    ] })
  ] });
};

// src/criteria/EnergyCertificate/useEnergyCertificate.ts
var useEnergyCertificate = () => {
  const { showEnergyCertificate } = useConditionalFilter();
  return showEnergyCertificate;
};

// src/helpers/templates/filters/energyTypes.template.ts
var createEnergyTypeOptions = (energyTypes = []) => energyTypes.map((value) => ({
  value,
  label: useTranslate(`filters.energy_type.checkbox.${value.toLowerCase()}`)
}));
var EnergyTypeFilterTestId = "searchmfe-energytype-testid";
var EnergyTypes = () => {
  const { energyTypes } = useTemplates().filters;
  const lokalisedTitle = useTranslate("filters.energy_type.title");
  const options = createEnergyTypeOptions(energyTypes);
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: EnergyTypeFilterTestId, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: lokalisedTitle }),
    /* @__PURE__ */ jsx(ControlledCheckboxGroup, { name: "energyTypes", label: { content: lokalisedTitle, isHidden: true }, items: options })
  ] });
};

// src/criteria/EnergyTypes/useEnergyTypes.ts
var useEnergyTypes = () => {
  const { showEnergyTypeFilter } = useConditionalFilter();
  return showEnergyTypeFilter;
};
var EstateSubTypesTestId = "searchmfe-estatesubtype-testid";
var EstateSubTypes = () => {
  const locale = useLocale();
  const { estateSubTypes } = useConditionalFilter();
  const { getValues } = useFormContext();
  const estateType = getValues("estateType");
  if (!estateType || estateSubTypes.length === 0)
    return null;
  const lokalisedTitle = useTranslate(`filters.estate_subtype.${estateType.toLowerCase()}.title`);
  const getLokalisedSubtitle = (key) => useTranslate(`filters.estate_subtype.${estateType.toLowerCase()}.checkbox.${key.toLowerCase()}`);
  const items = Object.values(estateSubTypes).map((value) => ({
    label: getLokalisedSubtitle(value),
    value
  })).sort((a, b) => a.label.localeCompare(b.label, locale));
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Divider, {}),
    /* @__PURE__ */ jsxs(Box, { width: "100%", testId: EstateSubTypesTestId, children: [
      /* @__PURE__ */ jsx(Title, { criteriaTitle: lokalisedTitle }),
      /* @__PURE__ */ jsx(
        ControlledCheckboxGroup,
        {
          name: "estateSubTypes",
          label: { content: lokalisedTitle, isHidden: true },
          items
        }
      )
    ] })
  ] });
};

// src/helpers/utils/sortEstateTypes.ts
var estateTypesOrder = {
  // Residential estate types
  House_Apartment: 1,
  House: 2,
  Apartment: 3,
  Senior: 4,
  // Commercial estate types
  Office: 10,
  Trading: 11,
  Gastronomy_Hotel: 12,
  Storage_Production: 13,
  Agriculture_Forestry: 14,
  // Shared estate types
  Plot: 101,
  Parking: 102,
  Miscellaneous: 103
};
var sortEstateTypes = (estateTypes) => estateTypes.sort((a, b) => estateTypesOrder[a] - estateTypesOrder[b]);
var EstateTypeTestId = "searchmfe-estatetype-testid";
var EstateTypeDropdownBase = (_props, forwardedRef) => {
  const { control } = useFormContext();
  const { estateTypes } = useConditionalFilter();
  const items = sortEstateTypes(estateTypes).map((value) => ({
    id: value,
    label: useTranslate(`filters.estate_type.checkbox.${value.toLowerCase()}`)
  }));
  return /* @__PURE__ */ jsx(Box, { ref: forwardedRef, children: /* @__PURE__ */ jsx(
    Controller,
    {
      name: "estateType",
      control,
      render: ({ field }) => /* @__PURE__ */ jsx(
        Dropdown,
        {
          ...field,
          testId: EstateTypeTestId,
          items,
          label: { content: "estate_type", isHidden: true }
        }
      )
    }
  ) });
};
var EstateTypeDropdown = forwardRef(EstateTypeDropdownBase);
var FeaturesOptions = [
  {
    group: "main" /* MAIN */,
    features: [
      {
        value: FormFeatureType.PARKING_GARAGE,
        translationKey: "filters.features.checkbox.parking_garage"
      },
      { value: FormFeatureType.BALCONY_TERRACE, translationKey: "filters.features.checkbox.balcony_terrace" },
      { value: FormFeatureType.GARDEN, translationKey: "filters.features.checkbox.garden" },
      { value: FormFeatureType.SWIMMING_POOL, translationKey: "filters.features.checkbox.swimming_pool" }
    ]
  },
  {
    group: "indoor" /* INDOOR */,
    features: [
      { value: FormFeatureType.BATHROOM_WINDOW, translationKey: "filters.features.checkbox.bathroom_with_window" },
      { value: FormFeatureType.BATHROOM_BATHTUB, translationKey: "filters.features.checkbox.bathroom_with_bathtub" },
      { value: FormFeatureType.CELLAR, translationKey: "filters.features.checkbox.cellar" },
      { value: FormFeatureType.FURNISHED, translationKey: "filters.features.checkbox.furnished" },
      { value: FormFeatureType.UNFURNISHED, translationKey: "filters.features.checkbox.unfurnished" },
      { value: FormFeatureType.KITCHEN_FULLY_EQUIPPED, translationKey: "filters.features.checkbox.built_in_kitchen" },
      { value: FormFeatureType.UNDERFLOOR, translationKey: "filters.features.checkbox.underfloor" }
    ]
  },
  {
    group: "usage" /* USAGE */,
    features: [
      { value: FormFeatureType.VACANT, translationKey: "filters.features.checkbox.vacant" },
      {
        value: FormFeatureType.NEED_OF_RENOVATION,
        translationKey: "filters.features.checkbox.to_renovate"
      },
      {
        value: FormFeatureType.PETS_ALLOWED,
        translationKey: "filters.features.checkbox.pets_allowed"
      },
      { value: FormFeatureType.COMMISSION_FREE, translationKey: "filters.features.checkbox.free_of_commission" },
      { value: FormFeatureType.ASSISTED_LIVING, translationKey: "filters.features.checkbox.assisted_living" }
    ]
  },
  {
    group: "accessibility" /* ACCESSIBILITY */,
    features: [
      {
        value: FormFeatureType.REDUCE_MOBILITY_ACCESS,
        translationKey: "filters.features.checkbox.reduced_mobility_access"
      },
      {
        value: FormFeatureType.ELEVATOR,
        translationKey: "filters.features.checkbox.elevator"
      }
    ]
  }
];
var createFeaturesOptions = (features = []) => {
  const generatedFeaturesOptions = [];
  for (const { group, features: featuresOfGroup } of FeaturesOptions) {
    const matchingFeatures = featuresOfGroup.filter(({ value }) => features.includes(value));
    if (matchingFeatures.length > 0) {
      generatedFeaturesOptions.push({
        group,
        features: matchingFeatures
      });
    }
  }
  return generatedFeaturesOptions;
};
var IncludedFeaturesTestId = "searchmfe-included-features-testid";
var featuresSubGroups = [
  {
    key: "main",
    itemsType: "main" /* MAIN */,
    label: "main features"
  },
  {
    key: "indoor",
    itemsType: "indoor" /* INDOOR */
  },
  {
    key: "usage",
    itemsType: "usage" /* USAGE */
  },
  {
    key: "accessibility",
    itemsType: "accessibility" /* ACCESSIBILITY */
  }
];
var IncludedFeatureFilters = () => {
  const { features } = useConditionalFilter();
  const getItems = (wantedGroup) => {
    const groupData = createFeaturesOptions(features)?.find((option) => option.group === wantedGroup);
    return groupData?.features?.map(({ value, translationKey }) => ({ label: useTranslate(translationKey), value })) ?? [];
  };
  const lokalisedTitle = useTranslate("filters.features.title.features_included");
  const getLokalisedSubtitle = (key) => useTranslate(`filters.features.subtitle.${key}`);
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: IncludedFeaturesTestId, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: lokalisedTitle }),
    featuresSubGroups.map(({ key, label, itemsType }) => ({ key, label, items: getItems(itemsType) })).filter(({ items }) => items.length > 0).map(({ key, label, items }) => (
      // @ts-ignore Label prop behaves weird when using two value options of types at the same time
      /* @__PURE__ */ jsx(
        ControlledCheckboxGroup,
        {
          name: "features",
          label: label ? { content: label, isHidden: true } : getLokalisedSubtitle(key),
          items
        },
        key
      )
    ))
  ] });
};

// src/criteria/Features/Included/useIncludedFeatures.ts
var useIncludedFeatures = () => {
  const { features = [] } = useConditionalFilter();
  return features.length > 0;
};

// src/helpers/templates/filters/floors.template.ts
var floorTypesLokalizedKeys = {
  Groundfloor: "ground_floor",
  Roof_Storey: "attic_floor",
  Others: "other_floors"
};
var createFloorsOptions = (floors = []) => floors.map((value) => ({
  value,
  label: useTranslate(`filters.floors.checkbox.${floorTypesLokalizedKeys[value]}`)
}));
var FloorsFilterTestId = "searchmfe-floors-filter-testid";
var FloorsFilter = () => {
  const { floors } = useConditionalFilter();
  const lokalisedTitle = useTranslate("filters.floors.title");
  const options = createFloorsOptions(floors);
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: FloorsFilterTestId, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: lokalisedTitle }),
    /* @__PURE__ */ jsx(ControlledCheckboxGroup, { name: "floors", label: { content: lokalisedTitle, isHidden: true }, items: options })
  ] });
};

// src/criteria/Floors/useFloorsFilter.ts
var useFloorsFilter = () => {
  const { showFloorsFilter } = useConditionalFilter();
  return showFloorsFilter;
};
var SubTitle = ({ criteriaSubTitle }) => /* @__PURE__ */ jsx(
  Box,
  {
    testId: `criteria-subtitle-${_2.kebabCase(criteriaSubTitle)}`,
    typography: "typography.body.14.bold",
    marginTop: "spacing.16",
    paddingBottom: "spacing.8",
    paddingTop: "spacing.8",
    children: criteriaSubTitle
  }
);
var ThreeStateOptions = [
  {
    value: ThreeStateType.YES,
    translationKey: "filters.common.button_group.include"
  },
  {
    value: ThreeStateType.ANY,
    translationKey: "filters.common.button_group.all"
  },
  {
    value: ThreeStateType.NO,
    translationKey: "filters.common.button_group.exclude"
  }
];
var ThreeStateButtonGroup = ({ label, ariaLabel = "", name }) => {
  const { control } = useFormContext();
  const items = ThreeStateOptions.map(({ value, translationKey }) => ({
    value,
    label: useTranslate(translationKey)
  }));
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    label && /* @__PURE__ */ jsx(SubTitle, { criteriaSubTitle: label }),
    /* @__PURE__ */ jsx(
      Controller,
      {
        name,
        control,
        render: ({ field }) => /* @__PURE__ */ jsx(ButtonGroup, { ...field, items, label: label ?? ariaLabel, type: "radio" })
      }
    )
  ] });
};
var GoodwillSaleTestId = "searchmfe-goodwill-sale-testid";
var GoodwillSale = () => {
  const lokalisedTitle = useTranslate("filters.features.title.is_sale_goodwill");
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: GoodwillSaleTestId, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: lokalisedTitle }),
    /* @__PURE__ */ jsx(ThreeStateButtonGroup, { name: "goodwillSale", ariaLabel: lokalisedTitle })
  ] });
};

// src/criteria/GoodwillSale/useGoodwillSale.ts
var useGoodwillSale = () => {
  const { showGoodwillSale } = useConditionalFilter();
  return showGoodwillSale;
};
var listingsDisplayLokalizedKeys = {
  [ListingsDisplayType.EXCLUSIVE_MANDATE]: "exclusive_mandate"
};
var createListingsDisplayOptions = (options = []) => options.map((value) => ({
  value,
  label: useTranslate(`filters.listings_display.checkbox.${listingsDisplayLokalizedKeys[value]}`)
}));
var ListingsDisplayTestId = "searchmfe-listing-display-testid";
var ListingsDisplay = () => {
  const { listingsDisplay } = useConditionalFilter();
  const lokalisedTitle = useTranslate("filters.listings_display.title");
  const options = createListingsDisplayOptions(listingsDisplay);
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: ListingsDisplayTestId, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: lokalisedTitle }),
    /* @__PURE__ */ jsx(
      ControlledCheckboxGroup,
      {
        name: "listingsDisplay",
        label: { content: lokalisedTitle, isHidden: true },
        items: options
      }
    )
  ] });
};

// src/criteria/ListingsDisplay/useListingsDisplay.ts
var useListingsDisplay = () => {
  const { showListingsDisplay } = useConditionalFilter();
  return showListingsDisplay;
};
var PlotRangeTestId = "searchmfe-spacePlotRangeplotrange-testid";
var PlotSpaceRange = () => {
  const spacePlotRangeTitle = useTranslate("filters.plot_area.title");
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: PlotRangeTestId, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: spacePlotRangeTitle }),
    /* @__PURE__ */ jsx(MinMaxRange, { criteria: "plotSpace", range: { min: 0, max: 999999999 }, suffix: "m\xB2" })
  ] });
};

// src/criteria/PlotSpace/usePlotSpaceRange.ts
var usePlotSpaceRange = () => {
  const { showPlotFilter } = useConditionalFilter();
  return showPlotFilter;
};
var PriceRangeTestId = "searchmfe-pricerange-testid";
var PriceRangeBase = (_props, forwardedRef) => {
  const priceTitle = useTranslate("filters.price_range.title");
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: PriceRangeTestId, ref: forwardedRef, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: priceTitle }),
    /* @__PURE__ */ jsx(MinMaxRange, { criteria: "price", icon: EuroSignIcon, range: { min: 0, max: 999999999 } })
  ] });
};
var PriceRange = forwardRef(PriceRangeBase);
var useProjectType = () => {
  const { projectTypes } = useConditionalFilter();
  return !isEmpty(projectTypes);
};
var projectTypeLokalizedKeys = {
  [FormProjectType.FLATSHARING]: "flat_sharing",
  [FormProjectType.SWAP_APARTMENT]: "swap_apartment",
  [FormProjectType.FORECLOSURE]: "foreclosure",
  [FormProjectType.INVESTMENT]: "investment",
  [FormProjectType.NEW_BUILD]: "new_builds",
  [FormProjectType.PROJECTED]: "projected",
  [FormProjectType.RESALE]: "resale",
  [FormProjectType.SHORT_TIME_RENTAL]: "temporary_living",
  [FormProjectType.STOCK]: "stock",
  [FormProjectType.LIFE_ANNUITY]: "life_annuity"
};
var createProjectTypeOptions = (projectTypes = []) => projectTypes.map((value) => ({
  value,
  label: useTranslate(`filters.project_type.checkbox.${projectTypeLokalizedKeys[value]}`)
}));

// src/helpers/utils/sortProjectTypes.ts
var projectTypesOrder = {
  New_Build: 1,
  Foreclosure: 2,
  Investment: 3,
  Life_Annuity: 4,
  Projected: 5,
  Resale: 6,
  Swap_Apartment: 7,
  Flatsharing: 8,
  Short_Time_Rental: 9,
  Stock: 10
};
var sortProjectTypes = (projectTypes) => projectTypes.sort((a, b) => projectTypesOrder[a] - projectTypesOrder[b]);
var ProjectTypeTestId = "searchmfe-projecttype-testid";
var ProjectType = () => {
  const { projectTypes } = useConditionalFilter();
  const lokalisedTitle = useTranslate("filters.project_type.title");
  const showProjectType = useProjectType();
  const isMobile = useIsMobile();
  const options = createProjectTypeOptions(sortProjectTypes(projectTypes));
  return showProjectType ? /* @__PURE__ */ jsx(Box, { rowGap: "spacing.none", children: /* @__PURE__ */ jsx(Box, { width: "100%", testId: ProjectTypeTestId, children: /* @__PURE__ */ jsx(ControlledCheckboxGroup, { name: "projectTypes", hasTwoColumns: !isMobile, label: lokalisedTitle, items: options }) }) }) : null;
};
var ProviderTypeTabsTestId = "searchmfe-providerType-testid";
var ProviderTypeTabsBase = (_props, forwardedRef) => {
  const providerTypeTabsTitle = useTranslate("filters.provider.title");
  const { providerType } = useConditionalFilter();
  const items = providerType.map((value) => ({
    label: useTranslate(`filters.provider.checkbox.${value.toLowerCase()}`),
    value
  }));
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: ProviderTypeTabsTestId, ref: forwardedRef, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: providerTypeTabsTitle }),
    /* @__PURE__ */ jsx(
      ControlledCheckboxGroup,
      {
        name: "providerType",
        label: { content: providerTypeTabsTitle, isHidden: true },
        items
      }
    )
  ] });
};
var ProviderTypeTabs = forwardRef(ProviderTypeTabsBase);

// src/criteria/ProviderTypeTabs/useProviderTypeTabs.ts
var useProviderType = () => {
  const { showProviderTypeFilter } = useConditionalFilter();
  return showProviderTypeFilter;
};
var RoomsRangeTestId = "searchmfe-roomsrange-testid";
var RoomsRangeBase = (_props, forwardedRef) => {
  const roomsTitle = useTranslate("filters.rooms_range.title");
  const roomsRangeMinLabel = useTranslate("filters.common.range.label.min");
  const roomsRangeMaxLabel = useTranslate("filters.common.range.label.max");
  const { watch } = useFormContext();
  const roomsAnyLabel = useTranslate("filters.rooms_range.dropdown.any");
  const roomsOptionsTemplate = useTemplates().filters.rooms;
  const roomsOptions = roomsOptionsTemplate?.filter((roomsType) => ROOMS_DICTIONARY[roomsType]).map((roomsType) => ({
    id: roomsType,
    label: ROOMS_DICTIONARY[roomsType].label
  }));
  const roomsMin = watch("rooms.min");
  const roomsMax = watch("rooms.max");
  const [minOptions, setMinOptions] = useState(roomsOptions);
  const [maxOptions, setMaxOptions] = useState(roomsOptions);
  const anyOption = { id: RoomsType.ANY, label: roomsAnyLabel };
  const filterOptions = (selectedValue, comparator, setOptions) => {
    const filterOption = (option) => comparator(ROOMS_DICTIONARY[option.id].value, ROOMS_DICTIONARY[selectedValue].value);
    const filteredOptions = selectedValue !== RoomsType.ANY ? roomsOptions.filter((option) => filterOption(option)) : [...roomsOptions];
    filteredOptions.unshift(anyOption);
    setOptions(filteredOptions);
  };
  useEffect(() => {
    filterOptions(roomsMax, _2.lte, setMinOptions);
    filterOptions(roomsMin, _2.gte, setMaxOptions);
  }, [roomsMin, roomsMax]);
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: RoomsRangeTestId, ref: forwardedRef, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: roomsTitle }),
    /* @__PURE__ */ jsx(
      MinMaxDropdownRange,
      {
        criteria: "rooms",
        labels: { min: roomsRangeMinLabel, max: roomsRangeMaxLabel },
        minOptions,
        maxOptions
      }
    )
  ] });
};
var RoomsRange = forwardRef(RoomsRangeBase);

// src/criteria/Rooms/useRoomsRange.ts
var useRoomsRange = () => {
  const { showRoomsFilter } = useConditionalFilter();
  return showRoomsFilter;
};
var SpaceRangeTestId = "searchmfe-squaremeterrange-testid";
var SpaceRangeBase = (_props, forwardedRef) => {
  const squareMeterTitle = useTranslate("filters.area.title");
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: SpaceRangeTestId, ref: forwardedRef, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: squareMeterTitle }),
    /* @__PURE__ */ jsx(MinMaxRange, { criteria: "space", range: { min: 0, max: 999999999 }, suffix: "m\xB2" })
  ] });
};
var SpaceRange = forwardRef(SpaceRangeBase);

// src/criteria/Space/Range/useSpaceRange.ts
var useSpaceRange = () => {
  const { showSpaceFilter } = useConditionalFilter();
  return showSpaceFilter;
};
var SubsidizingHousingTestId = "searchmfe-subsidizing-housing-testid";
var SubsidizingHousing = () => {
  const lokalisedTitle = useTranslate("filters.features.title.subsidizing_housing");
  return /* @__PURE__ */ jsxs(Box, { width: "100%", testId: SubsidizingHousingTestId, children: [
    /* @__PURE__ */ jsx(Title, { criteriaTitle: lokalisedTitle }),
    /* @__PURE__ */ jsx(ThreeStateButtonGroup, { name: "subsidizingHousing", ariaLabel: lokalisedTitle })
  ] });
};

// src/criteria/SubsidizingHousing/useSubsidizingHousing.ts
var useSubsidizingHousing = () => {
  const { showSubsidizingHousing } = useConditionalFilter();
  return showSubsidizingHousing;
};
var UsageTypeTestId = "searchmfe-usagetype-testid";
var UsageTypeTabsBase = (_props, forwardedRef) => {
  const { control } = useFormContext();
  const { usageTypes } = useConditionalFilter();
  const items = usageTypes.map((value) => ({
    id: value,
    label: useTranslate(`filters.context_switch.${value.toLowerCase()}`),
    content: null
  }));
  return /* @__PURE__ */ jsx(Box, { ref: forwardedRef, children: /* @__PURE__ */ jsx(
    Controller,
    {
      name: "usageType",
      control,
      render: ({ field }) => /* @__PURE__ */ jsx(Tabs, { ...field, testId: UsageTypeTestId, items, label: "usageType" })
    }
  ) });
};
var UsageTypeTabs = forwardRef(UsageTypeTabsBase);

// src/criteria/UsageType/useUsageTypeTabs.ts
var useUsageTypeTabs = () => {
  const { showUsageTypeTabs } = useConditionalFilter();
  return showUsageTypeTabs;
};
var FiltersContainer = ({ scrollInto }) => {
  const refs = useRef({});
  const showRoomsRange = useRoomsRange();
  const showBedroomsRange = useBedroomsRange();
  const showSpaceRange = useSpaceRange();
  const showPlotSpaceRange = usePlotSpaceRange();
  const showFeatures = useIncludedFeatures();
  const showSubsidizingHousing = useSubsidizingHousing();
  const showGoodwillSale = useGoodwillSale();
  const showFloorsFilter = useFloorsFilter();
  const showConstructionYearRange = useConstructionYearRange();
  const showProviderType = useProviderType();
  const showEnergyCertificate = useEnergyCertificate();
  const showListingsDisplay = useListingsDisplay();
  const showUsageTypeTabs = useUsageTypeTabs();
  const showEnergyTypeFilter = useEnergyTypes();
  const showDistributionTypeButtonGroupFilter = useDistributionTypeButtonGroupFilter();
  const assign = (element, type) => {
    refs.current[type] = element;
  };
  useScrollInto(scrollInto, refs);
  return /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "column", rowGap: "spacing.20", children: [
    showUsageTypeTabs && /* @__PURE__ */ jsx(UsageTypeTabs, { ref: (el) => assign(el, "usageType") }),
    /* @__PURE__ */ jsx(MultiLocationModalShortcut, {}),
    showDistributionTypeButtonGroupFilter && /* @__PURE__ */ jsx(DistributionTypeButtonGroup, {}),
    /* @__PURE__ */ jsx(EstateTypeDropdown, { ref: (el) => assign(el, "estateType") }),
    /* @__PURE__ */ jsx(ProjectType, {}),
    /* @__PURE__ */ jsx(Divider, {}),
    /* @__PURE__ */ jsx(PriceRange, { ref: (el) => assign(el, "priceRange") }),
    showRoomsRange && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(RoomsRange, { ref: (el) => assign(el, "roomsRange") })
    ] }),
    showBedroomsRange && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(BedroomsRange, {})
    ] }),
    showSpaceRange && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(SpaceRange, { ref: (el) => assign(el, "spaceRange") })
    ] }),
    showPlotSpaceRange && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(PlotSpaceRange, {})
    ] }),
    showFeatures && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(IncludedFeatureFilters, {})
    ] }),
    showSubsidizingHousing && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(SubsidizingHousing, {})
    ] }),
    showGoodwillSale && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(GoodwillSale, {})
    ] }),
    showFloorsFilter && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(FloorsFilter, {})
    ] }),
    showConstructionYearRange && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(ConstructionYearRange, {})
    ] }),
    showEnergyCertificate && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(EnergyCertificate, {})
    ] }),
    showEnergyTypeFilter && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(EnergyTypes, {})
    ] }),
    showProviderType && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(ProviderTypeTabs, {})
    ] }),
    showListingsDisplay && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Divider, {}),
      /* @__PURE__ */ jsx(ListingsDisplay, {})
    ] }),
    /* @__PURE__ */ jsx(EstateSubTypes, {})
  ] });
};
var FiltersModalTestId = "searchmfe-filters-modal-testid";
var FiltersModal = ({ scrollInto, trigger, title, customElement, ...rest }) => /* @__PURE__ */ jsxs(ModalContainer, { ...rest, trigger, title, testId: FiltersModalTestId, children: [
  customElement,
  /* @__PURE__ */ jsx(FiltersContainer, { scrollInto })
] });

export { FilterButton, FiltersModal, MultiLocationAutocompleteModal, getPolylines, useIsMobile, useSubmitButton };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=chunk-E5YV2QM5.js.map